(() => {
    "use strict";

    const HOME_STATE_KEY = "fr-home-state:v1";

    const explorer =
        document.querySelector("[data-home-minimal-explorer]");

    if (!explorer) return;

    const modeButtons = Array.from(
        explorer.querySelectorAll("[data-home-minimal-mode-button]")
    );

    const explorerPanel = explorer.querySelector(
        '[data-home-minimal-mode-panel="explorer"]'
    );

    const recentPanel = explorer.querySelector(
        '[data-home-minimal-mode-panel="recent"]'
    );

    if (!explorerPanel || !recentPanel) return;

    const panels = [explorerPanel, recentPanel];

    const rotatorNav = explorer.querySelector(
        "[data-home-minimal-rotator-nav]"
    );

    const rotatorBackButton = explorer.querySelector(
        "[data-home-rotator-back]"
    );

    const rotatorForwardButton = explorer.querySelector(
        "[data-home-rotator-forward]"
    );

    const recentToggleButton = explorer.querySelector(
        "[data-home-minimal-recent-toggle]"
    );

    const densityControl = explorer.querySelector(
        "[data-home-minimal-density-control]"
    );

    const densityButtons = Array.from(
        explorer.querySelectorAll(
            "[data-home-minimal-density-button]"
        )
    );

    const recentList =
        recentPanel.querySelector(".fr-home-recent");

    const rotator =
        explorer.querySelector("[data-home-rotator]") ||
        explorer.querySelector("[data-home-rotator2]");

    const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
    ).matches;

    let resizeTimer = null;


    // ==========================================================
    // HOME SESSION STATE
    // ==========================================================

    function readHomeState() {
        try {
            const raw =
                window.sessionStorage.getItem(
                    HOME_STATE_KEY
                );

            if (!raw) return {};

            const parsed = JSON.parse(raw);

            return (
                parsed &&
                typeof parsed === "object" &&
                !Array.isArray(parsed)
            )
                ? parsed
                : {};
        } catch (error) {
            console.warn(
                "Home state could not be read:",
                error
            );

            return {};
        }
    }

    function writeHomeState(changes) {
        try {
            const current =
                readHomeState();

            const next = {
                ...current,
                ...changes
            };

            window.sessionStorage.setItem(
                HOME_STATE_KEY,
                JSON.stringify(next)
            );
        } catch (error) {
            console.warn(
                "Home state could not be saved:",
                error
            );
        }
    }

    const savedState =
        readHomeState();


    // ==========================================================
    // INITIAL STATE
    // ==========================================================

    let currentMode =
        savedState.mode ||
        explorer.getAttribute(
            "data-home-minimal-mode"
        ) ||
        "explorer";

    /*
     * Density system intentionally retained for experimentation.
     *
     * User-facing Comfortable / Compact control is currently
     * hidden/removed. The site default is COMPACT.
     *
     * To experiment later, change this one value manually to:
     *
     *     "comfortable"
     *
     * or call setDensity("comfortable") from code.
     */
    let currentDensity = "compact";

    if (
        currentMode !== "explorer" &&
        currentMode !== "recent"
    ) {
        currentMode = "explorer";
    }

    if (
        currentDensity !== "comfortable" &&
        currentDensity !== "compact"
    ) {
        currentDensity = "compact";
    }


    // ==========================================================
    // BUILD SLIDER
    // ==========================================================

    const panelParent =
        explorerPanel.parentNode;

    if (
        !panelParent ||
        recentPanel.parentNode !== panelParent
    ) {
        console.warn(
            "Home slider: panels do not share a parent."
        );
        return;
    }

    const viewport =
        document.createElement("div");

    viewport.className =
        "fr-home-minimal-explorer__viewport";

    const track =
        document.createElement("div");

    track.className =
        "fr-home-minimal-explorer__track";

    panelParent.insertBefore(
        viewport,
        explorerPanel
    );

    viewport.appendChild(track);

    panels.forEach((panel) => {
        panel.hidden = false;

        panel.classList.add(
            "fr-home-minimal-explorer__slide"
        );

        track.appendChild(panel);
    });


    // ==========================================================
    // HEIGHT
    // ==========================================================

    function getCurrentPanel() {
        return currentMode === "recent"
            ? recentPanel
            : explorerPanel;
    }

    function syncViewportHeight(
        immediate = false
    ) {
        const panel =
            getCurrentPanel();

        if (!panel) return;

        const height = Math.ceil(
            panel
                .getBoundingClientRect()
                .height
        );

        if (!height) return;

        if (
            immediate ||
            reducedMotion
        ) {
            const oldTransition =
                viewport.style.transition;

            viewport.style.transition =
                "none";

            viewport.style.height =
                height + "px";

            void viewport.offsetHeight;

            viewport.style.transition =
                oldTransition;

            return;
        }

        viewport.style.height =
            height + "px";
    }


    // ==========================================================
    // DENSITY
    // ==========================================================

    function setDensity(
        density,
        save = true
    ) {
        if (
            density !== "comfortable" &&
            density !== "compact"
        ) {
            density = "compact";
        }

        currentDensity = density;

        if (recentList) {
            recentList.setAttribute(
                "data-density",
                density
            );
        }

        densityButtons.forEach(
            (button) => {
                const isActive =
                    button.getAttribute(
                        "data-home-minimal-density-button"
                    ) === density;

                button.classList.toggle(
                    "is-active",
                    isActive
                );

                button.setAttribute(
                    "aria-pressed",
                    isActive
                        ? "true"
                        : "false"
                );
            }
        );

        if (save) {
            writeHomeState({
                density
            });
        }

        window.requestAnimationFrame(
            () => {
                syncViewportHeight(
                    false
                );
            }
        );
    }


    // ==========================================================
    // ROTATOR HISTORY CONTROLS
    // ==========================================================

    function syncRotatorHistoryControls(
        detail = null
    ) {
        let canGoBack = false;

        if (
            detail &&
            typeof detail.canGoBack ===
            "boolean"
        ) {
            canGoBack =
                detail.canGoBack;
        } else if (rotator) {
            canGoBack =
                rotator.getAttribute(
                    "data-home-rotator-can-go-back"
                ) === "true";
        }

        if (rotatorBackButton) {
            rotatorBackButton.disabled =
                !canGoBack;

            rotatorBackButton.setAttribute(
                "aria-disabled",
                canGoBack
                    ? "false"
                    : "true"
            );
        }

        if (rotatorForwardButton) {
            rotatorForwardButton.disabled =
                !rotator;

            rotatorForwardButton.setAttribute(
                "aria-disabled",
                rotator
                    ? "false"
                    : "true"
            );
        }
    }


    // ==========================================================
    // MODE
    // ==========================================================

    function setMode(
        mode,
        immediate = false,
        save = true
    ) {
        if (
            mode !== "explorer" &&
            mode !== "recent"
        ) {
            mode = "explorer";
        }

        currentMode = mode;

        explorer.setAttribute(
            "data-home-minimal-mode",
            mode
        );

        modeButtons.forEach(
            (button) => {
                const isActive =
                    button.getAttribute(
                        "data-home-minimal-mode-button"
                    ) === mode;

                button.classList.toggle(
                    "is-active",
                    isActive
                );

                button.setAttribute(
                    "aria-selected",
                    isActive
                        ? "true"
                        : "false"
                );
            }
        );

        panels.forEach((panel) => {
            const isActive =
                panel.getAttribute(
                    "data-home-minimal-mode-panel"
                ) === mode;

            panel.setAttribute(
                "aria-hidden",
                isActive
                    ? "false"
                    : "true"
            );

            if ("inert" in panel) {
                panel.inert =
                    !isActive;
            }
        });

        if (rotatorNav) {
            rotatorNav.hidden =
                mode !== "explorer";
        }

        if (densityControl) {
            densityControl.hidden =
                mode !== "recent";
        }

        if (recentToggleButton) {
            const available =
                recentToggleButton
                    .dataset.available ===
                "true";

            recentToggleButton.hidden =
                mode !== "recent" ||
                !available;
        }

        if (save) {
            writeHomeState({
                mode
            });
        }

        window.requestAnimationFrame(
            () => {
                syncViewportHeight(
                    immediate
                );
            }
        );
    }


    // ==========================================================
    // EVENTS
    // ==========================================================

    modeButtons.forEach((button) => {
        button.addEventListener(
            "click",
            () => {
                const mode =
                    button.getAttribute(
                        "data-home-minimal-mode-button"
                    );

                if (mode) {
                    setMode(mode);
                }
            }
        );
    });

    densityButtons.forEach(
        (button) => {
            button.addEventListener(
                "click",
                () => {
                    const density =
                        button.getAttribute(
                            "data-home-minimal-density-button"
                        );

                    if (density) {
                        setDensity(
                            density
                        );
                    }
                }
            );
        }
    );

    if (
        rotatorBackButton &&
        rotator
    ) {
        rotatorBackButton.addEventListener(
            "click",
            () => {
                rotator.dispatchEvent(
                    new CustomEvent(
                        "fr-home-rotator-back"
                    )
                );
            }
        );
    }

    if (
        rotatorForwardButton &&
        rotator
    ) {
        rotatorForwardButton.addEventListener(
            "click",
            () => {
                rotator.dispatchEvent(
                    new CustomEvent(
                        "fr-home-rotator-forward"
                    )
                );
            }
        );
    }

    if (rotator) {
        rotator.addEventListener(
            "fr-home-rotator-history-change",
            (event) => {
                syncRotatorHistoryControls(
                    event.detail || null
                );
            }
        );
    }


    // ==========================================================
    // RECENT TOGGLE
    //
    // IMPORTANT:
    //
    // part-home-minimal-recent.hbs now owns the actual click
    // behavior for Show More / Show Less.
    //
    // This file only reacts to availability changes and lets
    // ResizeObserver update the slider height after rows change.
    //
    // Do NOT dispatch fr-home-recent-toggle here as well, or one
    // click will toggle twice and appear to do nothing.
    // ==========================================================

    if (recentToggleButton) {
        recentToggleButton.addEventListener(
            "fr-home-recent-availability",
            () => {
                setMode(
                    currentMode,
                    true,
                    false
                );
            }
        );
    }


    // ==========================================================
    // RESIZE
    // ==========================================================

    if (
        "ResizeObserver" in window
    ) {
        const observer =
            new ResizeObserver(() => {
                syncViewportHeight(
                    true
                );
            });

        panels.forEach((panel) => {
            observer.observe(panel);
        });
    }

    window.addEventListener(
        "resize",
        () => {
            window.clearTimeout(
                resizeTimer
            );

            resizeTimer =
                window.setTimeout(
                    () => {
                        syncViewportHeight(
                            true
                        );
                    },
                    120
                );
        }
    );

    if (
        document.fonts &&
        document.fonts.ready
    ) {
        document.fonts.ready.then(
            () => {
                syncViewportHeight(
                    true
                );
            }
        );
    }

    if (reducedMotion) {
        explorer.classList.add(
            "fr-home-minimal-explorer--reduced-motion"
        );
    }


    // ==========================================================
    // INITIALIZE
    // ==========================================================

    syncRotatorHistoryControls();

    setDensity(
        currentDensity,
        false
    );

    setMode(
        currentMode,
        true,
        false
    );
})();